import React, { useState, useEffect } from 'react'
import { Typography, Checkbox, Button } from 'antd'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import ModalWrapper from './ModalWrapper'
import { BasicModalProps } from '../../types/modals'
import { saveCookieConsent, getCookieConsent } from '../../utils/cookieUtils'
import './CookieConsentModal.css'
import './Modals.css'
import { useSmartNavigate } from '../../hooks/useSmartNavigate'

const { Title, Paragraph, Text } = Typography

interface CookieConsentModalProps extends BasicModalProps {
    onAcceptAll?: () => void
    onAcceptSelection?: (cookies: {
        necessary: boolean
        statistics: boolean
        marketing: boolean
    }) => void
    onOnlyNecessary?: () => void
}

export const CookieConsentModal: React.FC<CookieConsentModalProps> = ({
    isOpen,
    onClose,
    onAcceptAll,
    onAcceptSelection,
    onOnlyNecessary,
}) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const smartNavigate = useSmartNavigate()
    const [statistics, setStatistics] = useState(false)
    const [marketing, setMarketing] = useState(false)
    // Necessary cookies are always enabled and cannot be disabled

    // Load existing cookie consent preferences when modal opens
    useEffect(() => {
        if (isOpen) {
            const existingConsent = getCookieConsent()
            if (existingConsent) {
                setStatistics(existingConsent.statistics || false)
                setMarketing(existingConsent.marketing || false)
            } else {
                // Reset to defaults if no consent exists
                setStatistics(false)
                setMarketing(false)
            }
        }
    }, [isOpen])

    const handleCookiesClick = () => {
        if (onClose) {
            onClose()
        }
        smartNavigate('/privacy#cookies')
    }

    const handlePrivacyClick = () => {
        navigate('/privacy')
    }

    const handleImprintClick = () => {
        navigate('/imprint')
    }

    const handleAcceptAll = () => {
        saveCookieConsent({
            necessary: true,
            statistics: true,
            marketing: true,
        })
        // Here you can initialize analytics/marketing tools
        if (onAcceptAll) {
            onAcceptAll()
        }
        if (onClose) {
            onClose()
        }
    }

    const handleAcceptSelection = () => {
        const cookies = {
            necessary: true, // Always true
            statistics,
            marketing,
        }
        saveCookieConsent(cookies)
        // Initialize tools based on consent
        if (cookies.statistics) {
            // Initialize analytics
        }
        if (cookies.marketing) {
            // Initialize marketing tools
        }
        if (onAcceptSelection) {
            onAcceptSelection(cookies)
        }
        if (onClose) {
            onClose()
        }
    }

    const handleOnlyNecessary = () => {
        setStatistics(false)
        setMarketing(false)
        saveCookieConsent({
            necessary: true,
            statistics: false,
            marketing: false,
        })
        if (onOnlyNecessary) {
            onOnlyNecessary()
        }
        if (onClose) {
            onClose()
        }
    }

    return (
        <ModalWrapper
            isOpen={isOpen}
            onClose={onClose}
            width={800}
            className="cookie-consent-modal"
            closable={false}
            maskClosable={false}
        >
            <div className="modal-content">
                <div className="cookie-consent-container">
                    <Title className="cookie-title" level={2}>
                        {t('modals.cookieConsent.title')}
                    </Title>

                    <Paragraph className="cookie-description">
                        {t('modals.cookieConsent.description')}
                    </Paragraph>

                    <div className="cookie-categories">
                        <div className="cookie-category">
                            <Checkbox checked disabled>
                                <div className="cookie-label">
                                    <span className="cookie-label-main">
                                        {t(
                                            'modals.cookieConsent.necessary.title'
                                        )}
                                    </span>
                                    <span className="cookie-label-sub">
                                        {t(
                                            'modals.cookieConsent.necessary.description'
                                        )}
                                    </span>
                                </div>
                            </Checkbox>
                        </div>

                        <div className="cookie-category">
                            <Checkbox
                                checked={statistics}
                                onChange={(e) =>
                                    setStatistics(e.target.checked)
                                }
                            >
                                <div className="cookie-label">
                                    <span className="cookie-label-main">
                                        {t(
                                            'modals.cookieConsent.statistics.title'
                                        )}
                                    </span>
                                    <span className="cookie-label-sub">
                                        {t(
                                            'modals.cookieConsent.statistics.description'
                                        )}
                                    </span>
                                </div>
                            </Checkbox>
                        </div>

                        <div className="cookie-category">
                            <Checkbox
                                checked={marketing}
                                onChange={(e) => setMarketing(e.target.checked)}
                            >
                                <div className="cookie-label">
                                    <span className="cookie-label-main">
                                        {t(
                                            'modals.cookieConsent.marketing.title'
                                        )}
                                    </span>
                                    <span className="cookie-label-sub">
                                        {t(
                                            'modals.cookieConsent.marketing.description'
                                        )}
                                    </span>
                                </div>
                            </Checkbox>
                        </div>
                    </div>

                    <div className="cookie-buttons">
                        <Button
                            className="cookie-button cookie-button-secondary"
                            onClick={handleOnlyNecessary}
                        >
                            {t('modals.cookieConsent.onlyNecessary')}
                        </Button>
                        <Button
                            className="cookie-button cookie-button-secondary"
                            onClick={handleAcceptSelection}
                        >
                            {t('modals.cookieConsent.acceptSelection')}
                        </Button>
                        <Button
                            className="cookie-button-accept-all"
                            onClick={handleAcceptAll}
                        >
                            {t('modals.cookieConsent.acceptAll')}
                        </Button>
                    </div>

                    <div className="cookie-footer">
                        <Text
                            className="cookie-footer-link"
                            onClick={handleCookiesClick}
                        >
                            {t('modals.cookieConsent.cookies')}
                        </Text>
                        <Text className="cookie-footer-separator">|</Text>
                        <Text
                            className="cookie-footer-link"
                            onClick={handlePrivacyClick}
                        >
                            {t('modals.cookieConsent.privacyPolicy')}
                        </Text>
                        <Text className="cookie-footer-separator">|</Text>
                        <Text
                            className="cookie-footer-link"
                            onClick={handleImprintClick}
                        >
                            {t('modals.cookieConsent.imprint')}
                        </Text>
                    </div>
                </div>
            </div>
        </ModalWrapper>
    )
}
